// src/components/Education.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import "./Education.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";

const TABS = [
  "所有消息",
  "重要資訊",
  "衛教新知",
  "醫療服務",
  "人才招募",
  "活動相關",
];
const MOBILE_QUERY = "(max-width: 768px)";
// 行動版文章列表的最大高度（約兩篇的高度，可依觀感微調 150~180）
const MOBILE_LIST_MAX_PX = 160;

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}
function fmtDateTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("zh-TW", { hour12: false });
  } catch {
    return iso;
  }
}

export default function Education() {
  const q = useQuery();
  const navigate = useNavigate();
  const [tab, setTab] = useState("所有消息");
  const [all, setAll] = useState([]); // 全部公告（is_active = true）
  const [current, setCurrent] = useState(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined"
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  );

  // 監聽螢幕尺寸變化（決定只渲染哪一組分類 UI & 哪種版面）
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener?.("change", handler);
    setIsMobile(mql.matches);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  // 初次載入：拉全部（顯示中）
  useEffect(() => {
    supabase
      .from("notices")
      .select("*") // 顯示所有（含隱藏），是否能抓到取決於 RLS 與登入狀態
      .order("published_at", { ascending: false }) // 新→舊
      .limit(500)
      .then(({ data, error }) => {
        if (error) {
          console.error(error);
          setAll([]);
          return;
        }
        setAll(data || []);
      });
  }, []);

  // 依 URL ?id= 直接定位文章
  useEffect(() => {
    const id = q.get("id");
    if (id && all.length) {
      const row = all.find((r) => String(r.id) === String(id));
      if (row) {
        setCurrent(row);
        // 若該篇不在當前 tab，切回「所有消息」避免過濾掉
        if (tab !== "所有消息" && row.category !== tab) setTab("所有消息");
      }
    }
  }, [q, all]); // eslint-disable-line

  // 依 tab 過濾列表
  const filtered = useMemo(() => {
    return tab === "所有消息" ? all : all.filter((r) => r.category === tab);
  }, [tab, all]);

  // 當切換分類時，若 current 不屬於該分類，就改成該分類第一篇
  useEffect(() => {
    // 清單空 → 清空 current
    if (!filtered.length) {
      setCurrent(null);
      return;
    }
    // 若網址上有 id，優先讓「網址同步的 effect」處理，不要在這裡覆蓋
    const idInUrl = q.get("id");
    if (idInUrl) return;

    // 沒有網址 id：維持原本「同分類選第一篇」的行為，但避免不必要覆蓋
    setCurrent((prev) => {
      if (prev && filtered.some((r) => r.id === prev.id)) return prev;
      return filtered[0];
    });
  }, [filtered, q]); // 把 q 納入依賴，讓切換網址時此處能提早 return

  // 點擊選單外部或按 Esc → 自動關閉下拉分類（只在手機生效）
  useEffect(() => {
    if (!isMobile) return;

    const closeAll = () => {
      document
        .querySelectorAll(".edu-filter[open]")
        .forEach((d) => (d.open = false));
    };

    const onPointerDown = (e) => {
      // 如果點擊的不是 edu-filter 裡的元素，就關閉
      e.preventDefault();
      document.querySelectorAll(".edu-filter[open]").forEach((d) => {
        if (!d.contains(e.target)) d.open = false;
      });
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAll();
    };

    document.addEventListener("pointerdown", onPointerDown, true); // 用捕獲更穩
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isMobile]);

  // 目前在該分類列表中的索引（清單為「新→舊」）
  const currentIndex = useMemo(() => {
    if (!current) return -1;
    return filtered.findIndex((r) => r.id === current.id);
  }, [filtered, current]);

  // 依索引導向
  const gotoByIndex = (idx) => {
    if (idx < 0 || idx >= filtered.length) return;
    const n = filtered[idx];
    setCurrent(n);
    navigate(`/education?id=${n.id}`);
  };

  // 方向：上一篇=較新（index - 1），下一篇=較舊（index + 1）
  const hasPrev = currentIndex > 0; // 還有更「新」的一篇
  const hasNext = currentIndex >= 0 && currentIndex < filtered.length - 1; // 還有更「舊」的一篇
  const goPrev = () => hasPrev && gotoByIndex(currentIndex - 1);
  const goNext = () => hasNext && gotoByIndex(currentIndex + 1);
  const goTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  // 共用樣式（桌機/手機會有部份分支）
  const styles = {
    wrap: { maxWidth: 1080, margin: "24px auto", padding: "0 16px" },

    // 桌機分類標籤（固定列）
    tabsBar: { display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 },
    tab: (active) => ({
      padding: "8px 12px",
      borderRadius: 999,
      cursor: "pointer",
      border: active ? "1px solid #0ea5e9" : "1px solid #e5e7eb",
      background: active ? "#e0f2fe" : "#fff",
      color: active ? "#0c4a6e" : "#374151",
      fontWeight: 700,
    }),

    // 版面配置：桌機左右欄、手機上下堆疊
    layoutDesktop: {
      display: "grid",
      gridTemplateColumns: "280px 1fr",
      gap: 16,
    },
    layoutMobile: { display: "flex", flexDirection: "column", gap: 12 },

    // 左側列表卡片
    listCard: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      background: "#fff",
      overflow: "hidden",
    },
    listHead: {
      padding: "10px 12px",
      borderBottom: "1px solid #e5e7eb",
      background: "#f8fafc",
      fontWeight: 800,
    },
    list: {
      listStyle: "none",
      padding: 0,
      margin: 0,
      maxHeight: isMobile ? MOBILE_LIST_MAX_PX : 520, // 手機：約兩篇高，超過可捲動
      overflow: "auto",
      WebkitOverflowScrolling: "touch",
    },
    li: (active) => ({
      padding: "10px 12px",
      borderBottom: "1px solid #f1f5f9",
      cursor: "pointer",
      background: active ? "#f0f9ff" : "#fff",
    }),

    // 主內容卡片
    main: {
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      background: "#fff",
      padding: 16,
      minHeight: 300,
      display: "grid",
      gridTemplateRows: "auto auto 1fr auto", // 標題/中繼/內容/導覽
      gap: 8,
    },
    title: { fontSize: 22, fontWeight: 800, margin: "0 0 4px" },
    meta: { fontSize: 13, color: "#6b7280", marginBottom: 8 },
    cover: {
      maxWidth: "100%",
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      margin: "8px 0",
    },
    content: { lineHeight: 1.7, color: "#111827" },

    // 導覽列
    navBar: {
      marginTop: 12,
      display: "grid",
      gridTemplateColumns: "1fr auto 1fr",
      alignItems: "center",
      gap: 8,
    },

    navBtnLight: {
      appearance: "none",
      height: 36,
      padding: "0 12px",
      borderRadius: 999,
      border: "1px solid #bfe9ff",
      background: "#e6f7ff",
      color: "#0f4d69",
      fontWeight: 700,
      cursor: "pointer",
      transition: "transform 0.05s ease, filter 0.2s ease",
      flex: "0 0 auto",
    },
    navBtnLightDisabled: {
      opacity: 0.45,
      cursor: "not-allowed",
      filter: "grayscale(0.2)",
    },

    // 手機：下拉分類樣式
    filterBox: {
      display: isMobile ? "block" : "none",
      margin: "12px 0",
      background: "#ffffff",
      border: "1px solid #e5e7eb",
      borderRadius: 12,
      boxShadow: "0 2px 10px rgba(0,0,0,.04)",
    },
    filterSummary: {
      listStyle: "none",
      padding: "12px 14px",
      fontWeight: 800,
      fontSize: 16,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: 10,
      color: "#0c4a6e",
    },
    filterPanel: {
      padding: "10px 12px",
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      whiteSpace: "nowrap",
    },
    chip: (active) => ({
      display: "inline-block",
      marginRight: 10,
      marginBottom: 6,
      padding: "8px 12px",
      borderRadius: 999,
      border: `1px solid ${active ? "#0ea5e9" : "#e5e7eb"}`,
      background: active ? "#e0f2fe" : "#fff",
      color: active ? "#0c4a6e" : "#374151",
      fontWeight: 700,
      fontSize: 14,
    }),
  };

  return (
    <section style={styles.wrap}>
      <h2 style={{ margin: "0 0 12px", fontSize: 22, fontWeight: 800 }}>
        公告 / 衛教文章
      </h2>

      {/* 只在桌機渲染固定標籤列（避免兩排同時出現） */}
      {!isMobile && (
        <div className="edu-tabs" style={styles.tabsBar}>
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              className="edu-tab"
              style={styles.tab(t === tab)}
              aria-pressed={t === tab}
              onClick={() => setTab(t)}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {/* 只在手機渲染：下拉 + 右展橫向捲動 */}
      {isMobile && (
        <details className="edu-filter" style={styles.filterBox}>
          <summary aria-label="文章分類" style={styles.filterSummary}>
            文章分類：<span style={{ color: "#0c4a6e" }}>{tab}</span>
            <span aria-hidden="true" style={{ marginLeft: "auto" }}>
              ▾
            </span>
          </summary>
          <div className="edu-filter-panel" style={styles.filterPanel}>
            {TABS.map((t) => (
              <button
                key={`m-${t}`}
                type="button"
                className={`edu-chip ${t === tab ? "active" : ""}`}
                style={styles.chip(t === tab)}
                onClick={() => {
                  setTab(t);
                  // 選完就收起
                  const d = document.querySelector(".edu-filter");
                  if (d && d.open) d.open = false;
                }}
              >
                {t}
              </button>
            ))}
          </div>
        </details>
      )}

      {/* 版面：桌機左右欄；手機上下堆疊（列表在上、內容在下） */}
      <div style={isMobile ? styles.layoutMobile : styles.layoutDesktop}>
        {/* 左／上：文章列表（依當前 tab） */}
        <div style={styles.listCard}>
          <div style={styles.listHead}>{tab}（最新）</div>
          <ul style={styles.list}>
            {filtered.map((n) => (
              <li
                key={n.id}
                style={styles.li(current && n.id === current.id)}
                onClick={() => {
                  setCurrent(n);
                  navigate(`/education?id=${n.id}`);
                }}
              >
                <div style={{ marginBottom: 2 }}>
                  <div
                    style={{
                      display: "inline-block",
                      padding: "2px 8px",
                      borderRadius: 999,
                      background: catStyle(n.category).bg,
                      border: `1px solid ${catStyle(n.category).bd}`,
                      color: catStyle(n.category).fg,
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {n.category}
                  </div>
                  <div style={{ fontWeight: 700, marginTop: 6 }}>{n.title}</div>
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  {fmtDateTime(n.published_at)}
                </div>
              </li>
            ))}
            {!filtered.length && (
              <li style={{ padding: 12, color: "#6b7280" }}>此分類暫無文章</li>
            )}
          </ul>
        </div>

        {/* 右／下：主內容 */}
        <div style={styles.main}>
          {!current ? (
            <div style={{ color: "#6b7280" }}>尚未選擇文章</div>
          ) : (
            <>
              <div style={styles.title}>
                <div
                  style={{
                    display: "inline-block",
                    padding: "2px 8px",
                    borderRadius: 999,
                    background: catStyle(current.category).bg,
                    border: `1px solid ${catStyle(current.category).bd}`,
                    color: catStyle(current.category).fg,
                    fontSize: 14,
                    fontWeight: 700,
                    marginBottom: 8, // ★ 改成上下分行，留出間距
                  }}
                >
                  {current.category}
                </div>
                <div>{current.title}</div>
              </div>
              <div style={styles.meta}>
                發布：{fmtDateTime(current.published_at)}
                {current.author ? `　作者：${current.author}` : ""}
              </div>
              {current.image_url && (
                <img alt="cover" src={current.image_url} style={styles.cover} />
              )}
              <div style={styles.content} className="markdown-body">
                <ReactMarkdown
                  // 支援 GFM：表格、待辦清單、刪除線等
                  remarkPlugins={[remarkGfm]}
                  // 允許受控的 raw HTML，再用 sanitize 白名單清洗
                  rehypePlugins={[rehypeRaw, rehypeSanitize]}
                  // 視覺/可用性微調
                  components={{
                    a: ({ node, ...props }) => (
                      <a {...props} target="_blank" rel="noopener noreferrer" />
                    ),
                    img: ({ node, ...props }) => (
                      <img
                        {...props}
                        style={{
                          maxWidth: "100%",
                          borderRadius: 8,
                          border: "1px solid #e5e7eb",
                        }}
                        loading="lazy"
                        alt={props.alt || "image"}
                      />
                    ),
                    table: ({ node, ...props }) => (
                      <div
                        className="md-table-scroll"
                        role="region"
                        aria-label="可左右捲動的表格"
                      >
                        <table {...props} className="md-table" />
                      </div>
                    ),
                    th: ({ node, ...props }) => (
                      <th
                        {...props}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "8px 12px",
                          background: "#f8fafc",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      />
                    ),
                    td: ({ node, ...props }) => (
                      <td
                        {...props}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "8px 12px",
                          textAlign: "center",
                        }}
                      />
                    ),
                    code: ({ inline, children, ...props }) => {
                      const text = String(children || "");
                      const onlySpaces = text.replace(/\s/g, "") === "";

                      if (onlySpaces) {
                        // 內容只有空白 → 直接忽略，不渲染深色區塊
                        return inline ? <span /> : <br />;
                      }

                      return inline ? (
                        <code
                          {...props}
                          style={{
                            background: "#f1f5f9",
                            borderRadius: 4,
                            padding: "0 4px",
                          }}
                        >
                          {children}
                        </code>
                      ) : (
                        <pre
                          style={{
                            background: "#0b1220",
                            color: "#e5e7eb",
                            borderRadius: 8,
                            padding: 12,
                            overflow: "auto",
                          }}
                        >
                          <code {...props}>{children}</code>
                        </pre>
                      );
                    },
                  }}
                >
                  {current.content || ""}
                </ReactMarkdown>
              </div>

              {/* 文章導覽：上一篇=較新；下一篇=較舊 */}
              <div style={styles.navBar}>
                {/* 左：上一篇 */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "flex-start",
                  }}
                >
                  <button
                    className="edu-nav-btn"
                    onClick={goPrev}
                    style={{
                      ...styles.navBtnLight,
                      ...(hasPrev ? {} : styles.navBtnLightDisabled),
                    }}
                    disabled={!hasPrev}
                    title="查看較新的文章"
                  >
                    ⬅ 上一篇
                  </button>
                </div>

                {/* 中：回到頂部（固定寬度、不伸展） */}
                <div
                  style={{ flex: 0, display: "flex", justifyContent: "center" }}
                >
                  <button
                    className="edu-nav-btn"
                    onClick={scrollToTop}
                    style={styles.navBtnLight}
                    title="回到頁面頂部"
                    aria-label="回到頂部"
                  >
                    ↑ 回到頂部
                  </button>
                </div>

                {/* 右：下一篇 */}
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    className="edu-nav-btn"
                    onClick={goNext}
                    style={{
                      ...styles.navBtnLight,
                      ...(hasNext ? {} : styles.navBtnLightDisabled),
                    }}
                    disabled={!hasNext}
                    title="查看較舊的文章"
                  >
                    下一篇 ➡
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function catStyle(cat) {
  switch (cat) {
    case "重要資訊":
      return { bg: "#fee2e2", bd: "#fecaca", fg: "#991b1b" };
    case "衛教新知":
      return { bg: "#fef9c3", bd: "#fde68a", fg: "#92400e" };
    case "醫療服務":
      return { bg: "#e0f2fe", bd: "#bae6fd", fg: "#0c4a6e" };
    case "人才招募":
      return { bg: "#ede9fe", bd: "#ddd6fe", fg: "#5b21b6" };
    case "活動相關":
      return { bg: "#dcfce7", bd: "#bbf7d0", fg: "#166534" };
    default:
      return { bg: "#f1f5f9", bd: "#e2e8f0", fg: "#334155" };
  }
}
